/*
  This code is the intellectual property of Bulb Inc. and other software publishers;
  it may not be altered, copied or disclosed without prior written approval.
  Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { Injectable, Inject } from '@angular/core';
import type UppyT from '@uppy/core';
import { AppConfigService } from 'src/common/services/app-config/app-config.service';
import { BulbFileRestrictions, UppyAllowedFileTypes } from './types';
import { DEFAULT_FILE_RESTRICTIONS } from './uppy-config';

/**
 * Shape of config required for uppy upload instance.
 */
type UppyConfig = {
  debug: any;
  autoProceed:boolean;
  meta: { accountId: any; };
  restrictions: {
    maxFileSize: any;
    maxNumberOfFiles: number,
    allowedFileTypes: UppyAllowedFileTypes[];
  };
  onBeforeFileAdded: any;
};

/**
 * Imports and initializes a new instance of Uppy with the given config.
 */
@Injectable({
  providedIn: 'root',
})
export class UppyCoreService {

  private readonly uppyOptions: UppyConfig;

  public companionUrl: string;

  constructor(
    @Inject('BulbAuthService') private bulbAuthService,
    private ConfigService: AppConfigService,
  ) {
    this.companionUrl = this.ConfigService.uppy.companionURL;
    this.uppyOptions = {
      debug: this.ConfigService.uppy.debug,
      autoProceed: false,
      meta: { accountId: bulbAuthService.currentUser.id },
      restrictions: {
        maxFileSize: this.ConfigService.uppy.maxFileSize,
        maxNumberOfFiles: DEFAULT_FILE_RESTRICTIONS.maxNumberOfFiles,
        allowedFileTypes: DEFAULT_FILE_RESTRICTIONS.allowedFileTypes,
      },
      onBeforeFileAdded: (currentFile) => {
        let name = currentFile.name.replace(/[^A-Za-z0-9-.]/g, '');
        if (name.length === 0) name = 'upload';

        return {
          ...currentFile,
          meta: {
            ...currentFile.meta,
            name,
          },
          name,
        };
      },
    };
  }

  getConfigWithRestrictions({ maxNumberOfFiles, allowedFileTypes, autoProceed }: BulbFileRestrictions): UppyConfig {
    const options = this.uppyOptions;
    options.autoProceed = autoProceed;
    options.restrictions = {
      ...options.restrictions,
      maxNumberOfFiles,
      allowedFileTypes,
    };
    return options;
  }

  async initializeUppy({
    maxNumberOfFiles,
    allowedFileTypes,
    autoProceed,
  }): Promise<UppyT> {
    // Lazy load uppy and its dependencies.
    return Promise.all([
      import('@uppy/core'),
      import('@uppy/aws-s3'),
    ]).then(async ([UppyCore, AwsS3]) => {
      const Uppy = UppyCore.default;
      const UppyConfig = this.getConfigWithRestrictions({
        maxNumberOfFiles,
        allowedFileTypes,
        autoProceed,
      });

      return new Uppy(UppyConfig)
        .use(AwsS3.default, {
          companionUrl: this.companionUrl,
          allowedMetaFields: ['accountId'],
        });
    });
  }

}
